import { z } from 'zod'
import { addMinutes, isSameDay, parse, parseISO } from 'date-fns'

export type StepIndex = 0 | 1 | 2

const MIN_LEAD_MINUTES = 15

export const positionSchema = z.object({
  skillName: z.string(),
  startTime: z.string().min(1, 'Pick a start time'),
  duration: z.number().min(1, 'Set a duration'),
})

export type Position = z.infer<typeof positionSchema>

// startTime validity depends on the shift date, so it lives here rather than on
// positionSchema.shape.startTime — a same-day shift must start >= 15 min out.
export const validateStartTime = (
  startTime: string,
  date: string,
): string | undefined => {
  const base = positionSchema.shape.startTime.safeParse(startTime)
  if (!base.success) return base.error.issues[0]?.message
  if (!date || !isSameDay(parseISO(date), new Date())) return undefined
  const t = parse(startTime, 'HH:mm', new Date())
  if (t < addMinutes(new Date(), MIN_LEAD_MINUTES))
    return `Start time must be at least ${MIN_LEAD_MINUTES} min from now`
  return undefined
}

export const resolvedAddressSchema = z.object({
  street_address: z.string().min(1),
  city: z.string().min(1),
  state_code: z.string().min(1).max(2),
  postal_code: z.string().min(1),
  country: z.string().min(1),
  coordinates: z.object({ lat: z.number(), lng: z.number() }),
  places_id: z.string().min(1),
})

export type ResolvedAddress = z.infer<typeof resolvedAddressSchema>

export const checkoutSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  email: z.email('Enter a valid email'),
  phone: z
    .string()
    .refine(
      (v) => /^[\d\s().+-]+$/.test(v) && v.replace(/\D/g, '').length >= 7,
      'Enter a valid phone number',
    ),
  company: z.string().min(1, 'Required'),
  address: z.string().min(1, 'Required'),
  resolvedAddress: resolvedAddressSchema.nullable(),
  date: z.string().min(1, 'Pick a date'),
  positions: z.array(positionSchema).min(1),
})

export type CheckoutFields = z.input<typeof checkoutSchema>

export const STEP_LABELS: Record<StepIndex, string> = {
  0: 'Business & Contact',
  1: 'Shift Verification',
  2: 'Payment Method',
}
